import { useMemo, useRef, useState, type RefObject } from 'react';

import { Textarea } from '@/components/ui/textarea';
import { useMembers, type Member } from '@/features/members';
import { cn } from '@/lib/utils';
import { findMentionTrigger, insertMention } from '../mention';

interface MentionTextareaProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  rows?: number;
  className?: string;
  /** Inline label for ARIA when the surrounding form doesn't supply one. */
  ariaLabel?: string;
  /** Restrict the @mention list to these members (e.g. a project's members).
   *  Falls back to all org members when omitted. */
  members?: Member[];
  /** The textarea, for a caller that moves focus into it (e.g. on Reply). */
  textareaRef?: RefObject<HTMLTextAreaElement | null>;
}

function memberName(m: Member): string {
  return m.user.full_name || m.user.email;
}

/**
 * Textarea with a lightweight `@mention` autocomplete. Members are loaded
 * once (small org-scoped list, F6 — server data via React Query). Picking a
 * suggestion writes a `@[Name](user:<id>)` token. The token is what the
 * server parses for notifications.
 */
export function MentionTextarea({
  value,
  onChange,
  placeholder,
  rows = 3,
  className,
  ariaLabel,
  members: scopedMembers,
  textareaRef,
}: MentionTextareaProps) {
  const ownRef = useRef<HTMLTextAreaElement>(null);
  const ref = textareaRef ?? ownRef;
  // Only fetch the org-wide list when no scoped list was supplied.
  const { data: membersData } = useMembers({}, scopedMembers == null);

  const [trigger, setTrigger] = useState<{ start: number; partial: string } | null>(null);
  const [hoverIdx, setHoverIdx] = useState(0);

  const members = useMemo(
    () => (scopedMembers ?? membersData?.results ?? []).filter((m) => m.is_active),
    [scopedMembers, membersData],
  );

  // Filter candidates by the partial after `@`.
  const candidates = useMemo(() => {
    if (!trigger) return [];
    const q = trigger.partial.toLowerCase();
    return members
      .filter((m) =>
        memberName(m).toLowerCase().includes(q) || m.user.email.toLowerCase().includes(q),
      )
      .slice(0, 6);
  }, [trigger, members]);

  // Reset highlight when the candidate list changes (render-phase pattern;
  // avoids a useEffect just to call setState).
  const [trackedLen, setTrackedLen] = useState(0);
  if (candidates.length !== trackedLen) {
    setTrackedLen(candidates.length);
    if (hoverIdx !== 0) setHoverIdx(0);
  }

  function syncTrigger(next: string, caret: number) {
    setTrigger(findMentionTrigger(next, caret));
  }

  function commit(member: Member) {
    if (!trigger || !ref.current) return;
    const caret = ref.current.selectionStart ?? value.length;
    const { body, caret: nextCaret } = insertMention(
      value,
      trigger,
      caret,
      memberName(member),
      member.user.id,
    );
    onChange(body);
    setTrigger(null);
    // Move the caret after the inserted token on the next paint.
    requestAnimationFrame(() => {
      if (ref.current) {
        ref.current.focus();
        ref.current.setSelectionRange(nextCaret, nextCaret);
      }
    });
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (!trigger || candidates.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHoverIdx((i) => (i + 1) % candidates.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHoverIdx((i) => (i - 1 + candidates.length) % candidates.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      commit(candidates[hoverIdx]);
    } else if (e.key === 'Escape') {
      setTrigger(null);
    }
  }

  return (
    <div className="relative">
      <Textarea
        ref={ref}
        value={value}
        rows={rows}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className={className}
        onChange={(e) => {
          const next = e.target.value;
          onChange(next);
          syncTrigger(next, e.target.selectionStart ?? next.length);
        }}
        onKeyUp={(e) => {
          // Track caret movement (arrow keys, click) for trigger recomputation.
          const t = e.currentTarget;
          syncTrigger(t.value, t.selectionStart ?? t.value.length);
        }}
        onClick={(e) => {
          const t = e.currentTarget;
          syncTrigger(t.value, t.selectionStart ?? t.value.length);
        }}
        onBlur={() => {
          // Defer so a candidate click can fire before the popover unmounts.
          setTimeout(() => setTrigger(null), 100);
        }}
        onKeyDown={onKeyDown}
      />
      {trigger && candidates.length > 0 ? (
        <ul
          role="listbox"
          aria-label="Mention suggestions"
          className="bg-popover absolute left-2 z-10 mt-1 w-64 overflow-hidden rounded-md border shadow-md"
        >
          {candidates.map((m, i) => (
            <li
              key={m.id}
              role="option"
              aria-selected={i === hoverIdx}
              onMouseDown={(e) => {
                // mousedown fires before blur — pick the entry without losing focus.
                e.preventDefault();
                commit(m);
              }}
              onMouseEnter={() => setHoverIdx(i)}
              className={cn(
                'cursor-pointer px-3 py-2 text-sm',
                i === hoverIdx ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50',
              )}
            >
              <div className="font-medium">{memberName(m)}</div>
              <div className="text-muted-foreground text-xs">{m.user.email}</div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
